import React from 'react';
import styled, { css } from 'styled-components';
import { ButtonVariant, ButtonSize } from '../../types';

interface ButtonProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
}

const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return css`
        background: ${({ theme }) => theme.colors.primary};
        color: ${({ theme }) => theme.colors.white};
        
        &:hover:not(:disabled) {
          background: ${({ theme }) => theme.colors.primaryDark};
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(76, 175, 80, 0.3);
        }
      `;
    case 'secondary':
      return css`
        background: ${({ theme }) => theme.colors.secondary};
        color: ${({ theme }) => theme.colors.white};
        
        &:hover:not(:disabled) {
          background: #555;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 102, 102, 0.3);
        }
      `;
    case 'outline':
      return css`
        background: transparent;
        color: ${({ theme }) => theme.colors.primary};
        border: 2px solid ${({ theme }) => theme.colors.primary};
        
        &:hover:not(:disabled) {
          background: ${({ theme }) => theme.colors.primary};
          color: ${({ theme }) => theme.colors.white};
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(76, 175, 80, 0.3);
        }
      `;
    default:
      return css`
        background: ${({ theme }) => theme.colors.primary};
        color: ${({ theme }) => theme.colors.white};
      `;
  }
};

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'small':
      return css`
        padding: 8px 16px;
        font-size: ${({ theme }) => theme.typography.fontSize.sm};
        min-height: 36px;
        
        @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
          padding: 6px 12px;
          font-size: ${({ theme }) => theme.typography.fontSize.xs};
          min-height: 32px;
        }
      `;
    case 'large':
      return css`
        padding: 16px 32px;
        font-size: ${({ theme }) => theme.typography.fontSize.lg};
        min-height: 52px;
        
        @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
          padding: 14px 28px;
          font-size: ${({ theme }) => theme.typography.fontSize.base};
          min-height: 48px;
        }
      `;
    case 'medium':
    default:
      return css`
        padding: 12px 24px;
        font-size: ${({ theme }) => theme.typography.fontSize.base};
        min-height: 44px;
      `;
  }
};

const StyledButton = styled.button<{
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-decoration: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.ease};
  line-height: 1;
  width: ${({ fullWidth }) => fullWidth ? '100%' : 'auto'};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }

  ${({ variant }) => getVariantStyles(variant)}
  ${({ size }) => getSizeStyles(size)}
`;

const StyledLink = styled.a<{
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-decoration: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.ease};
  line-height: 1;
  width: ${({ fullWidth }) => fullWidth ? '100%' : 'auto'};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }

  ${({ variant }) => getVariantStyles(variant)}
  ${({ size }) => getSizeStyles(size)}
`;

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  onClick,
  href,
  disabled = false,
  className = '',
  type = 'button',
  fullWidth = false,
}) => {
  const commonProps = {
    variant,
    size,
    fullWidth,
    className,
    onClick,
    'aria-disabled': disabled,
  };

  if (href) {
    return (
      <StyledLink
        href={href}
        {...commonProps}
      >
        {children}
      </StyledLink>
    );
  }

  return (
    <StyledButton
      type={type}
      disabled={disabled}
      {...commonProps}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
